import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { CitiesService } from '../../../core/services/cities/cities.service';
import { PropertiesCityDetails } from '../../../core/interfaces/city';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { AvailabilityDatesService } from '../../../core/services/availability-dates/availability-dates.service';
import { MessageService } from '../../../core/services/messages/messages.service';
import { CookieAppService } from '@shared/common/services/cookie-app/cookie-app.service';
import SimpleBar from 'simplebar';
import { TranslationSlab } from '../../../core/classes/translation-slab/translation-slab';
import { LocationService } from '../../../core/services/location/location.service';
import { languages } from '../../../constants/language.constants';
import { LocalizationService } from '../../../core/services/localization/localization.service';
import { ResizeService } from '../../../core/services/resize/resize.service';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { PropertyParamsService } from '../../../core/services/property-params/property-params.service';
import { IListingData } from '../../../core/interfaces/listings';
import { IBookingParams } from '../../../core/interfaces/booking-params';
import { TrackingNewService } from '../../../core/services/tracking/tracking-new.service';

@Component({
  selector: 'app-city-picker',
  templateUrl: './city-picker.component.html',
  styleUrls: ['./city-picker.component.scss'],
  standalone: false
})
export class CityPickerComponent extends TranslationSlab implements OnInit, OnDestroy {
  cities: PropertiesCityDetails[];
  filteredCities: PropertiesCityDetails[];
  languages = languages;
  city: PropertiesCityDetails;
  cityIndex = -1;
  showCityPicker = false;
  searchModel = '';
  cityId: number;
  scrollbar: SimpleBar;
  imgixHost = environment.imgixHost;
  emptyResults: boolean;

  private searchField: ElementRef;
  private readonly _destruction$: Subject<void> = new Subject<void>();

  get isElements(): boolean {
    return !!(window as any).__isAngularElement__;
  }

  @Output() showCityPickerEvent: EventEmitter<boolean>;
  @Output() selectedCity: EventEmitter<PropertiesCityDetails>;

  @Input() set selectedCityId(cityId: number) {
    this.cityId = cityId;
    this.setSelectedCity();
  }
  @Input() classList: string;
  @Input() labelText: string = 'place-location-select';
  @Input() searchPlaceholderText: string = 'place-location-select';
  @Input() isHeaderSearch: boolean;
  @Input() parentShadowRoot: ShadowRoot;
  @Input() isListingRedirection: boolean;
  @Input() isPDPTracking: boolean;
  @ViewChild('simpleScrollBar', { static: false }) simpleScrollBar: ElementRef;
  @ViewChild('searchField') set searchFieldContent(content: ElementRef) {
    this.searchField = content;
  }

  constructor(
    public translateService: TranslateService,
    public localizationService: LocalizationService,
    public cookieAppService: CookieAppService,
    public locationService: LocationService,
    public trackingNewService: TrackingNewService,
    @Inject(PLATFORM_ID) public platformId: Object,
    private readonly citiesService: CitiesService,
    private readonly elemRef: ElementRef,
    private readonly availabilityDatesService: AvailabilityDatesService,
    private readonly messageService: MessageService,
    private readonly viewportService: ViewportService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly resizeService: ResizeService,
    private readonly propertyParamsService: PropertyParamsService
  ) {
    super(translateService, cookieAppService, locationService, platformId);

    this.cities = [];
    this.selectedCity = new EventEmitter();
    this.showCityPickerEvent = new EventEmitter();
  }

  ngOnInit() {
    this.getCitiesList();
    this.onLangChangeSubscription();
  }

  ngOnDestroy() {
    this._destruction$.next();
    this._destruction$.complete();
  }

  get isMobile() {
    return this.viewportService.isMobileDevice();
  }

  sortCitiesList() {
    // Selected language
    const language = this.translateService.currentLang;

    // Group the cities by country
    const groupedData = [...this.cities].reduce((acc: any, obj: any) => {
      const countryName = obj.country[language];
      if (!acc[countryName]) {
        acc[countryName] = [];
      }
      acc[countryName].push(obj);
      return acc;
    }, {});

    // Sort the cities within each country
    const sortedData = Object.values(groupedData).map((cities: any[]) =>
      cities.sort((a, b) => {
        const cityA = a.city[language] || '';
        const cityB = b.city[language] || '';
        return cityA.localeCompare(cityB);
      })
    );

    return [].concat(...sortedData);
  }

  getCitiesList() {
    this.citiesService
      .cities()
      .pipe(takeUntil(this._destruction$))
      .subscribe((cities: PropertiesCityDetails[]) => {
        this.cities = cities;
        this.assignCityValues();
        this.selectCityById(this.cityId);
      });
  }

  onLangChangeSubscription() {
    this.translateService.onLangChange.pipe(takeUntil(this._destruction$)).subscribe(() => {
      this.assignCityValues();
    });
  }

  assignCityValues() {
    this.cities = this.sortCitiesList();
    this.filteredCities = [...this.cities];
  }

  setSelectedCity(): void {
    this.cities.map((val) => {
      if (val.id == this.cityId) {
        this.city = val;
      }
    });
  }

  /**
   * @description
   * Close Drop Down by click outside
   */
  @HostListener('document:click', ['$event'])
  clickOutsideDropDown(event) {
    if (!this.elemRef.nativeElement.contains(event.composedPath()[0])) {
      if (this.showCityPicker) {
        this.showHideCityPicker();
      }
    }
  }

  /**
   * @description
   * Clear search field
   */
  clearSearchField(searchField: any) {
    if (searchField.value) {
      searchField.value = '';
      this.emptyResults = false;
      this.searchModel = searchField.value;
      this.filteredCities = [...this.cities];
      setTimeout(() => searchField.focus());
    }
  }

  /**
   * @description
   * Updates the selected city selection and emits the new value
   */
  selectCity(city: PropertiesCityDetails, index: number) {
    if (this.isListingRedirection) {
      this.redirectToListings(city);
    }

    this.city = city;
    this.cityIndex = index;
    this.selectedCity.emit(this.city);
    this.availabilityDatesService.selectedCityId.next(this.city.id);
    this.showHideCityPicker();
  }

  /**
   * @description
   * Redirect to Listings page (only from pdp)
   */
  redirectToListings(city: PropertiesCityDetails): void {
    const pdpData: IBookingParams = this.locationService.urlParamsToObj(this.locationService.getPathParams());
    let listingData: IListingData;

    listingData = {
      cityId: city.id,
      selectedDates: null,
      rooms: pdpData.rooms,
      guests: pdpData.guests,
      childrenAges: pdpData.childrenAges
    };

    if (pdpData.checkin && pdpData.checkout) {
      listingData.selectedDates = [new Date(pdpData.checkin), new Date(pdpData.checkout)];
    }

    this.propertyParamsService.redirectToPropertiesListing({
      ...listingData,
      route: `/listings/properties`
    });
  }

  /**
   * @description
   * Shows and hides the city picker
   */
  showHideCityPicker(): void {
    this.showCityPicker = !this.showCityPicker;
    this.messageService.updateOverlayStatus(this.showCityPicker, this.showCityPicker);
    this.showCityPickerEvent.emit(this.showCityPicker);
    this.resetSearchFilter();

    if (!this.showCityPicker) {
      this.emptyResults = false;
      this.unmountScrollbar();
      return;
    }

    this.initScrollbar();

    this.scrollToSelectedCity();

    setTimeout(() => this.searchField?.nativeElement.focus());

    if (this.isPDPTracking) {
      const pdpData: IBookingParams = this.locationService.urlParamsToObj(this.locationService.getPathParams());

      this.trackingNewService.trackClickEvent({
        page_name: 'product_details',
        element_id: 'product_details_city_picker_header_dropdown',
        destination_details: {
          city_name: this.city?.city.en,
          city_id: this.cityId,
          country_code: this.city?.code
        },
        reservation_details: {
          adults_number: pdpData.guests,
          children_number: pdpData.children,
          arrival_date: pdpData.checkin,
          departure_date: pdpData.checkout
        }
      });
    }
  }

  selectCityById(cityId: number) {
    for (let i = 0; i < this.cities.length; i++) {
      if (this.cities[i].id == cityId) {
        this.city = this.cities[i];
        this.cityIndex = i;
        this.selectedCity.emit(this.city);
      }
    }
  }

  /**
   * @description
   * Functionality for searching cities
   */
  searchCities(): void {
    this.filteredCities = [...this.cities];

    const normalizedSearch = this.normalizeText(this.searchModel).toLowerCase();

    this.filteredCities = this.filteredCities.filter((item) => {
      const cityName = this.normalizeText(this.localizationService.localize(item.city)).toLowerCase();
      const countryName = this.normalizeText(this.localizationService.localize(item.country)).toLowerCase();

      // Check if the search criteria matches either city name or country name
      return cityName.includes(normalizedSearch) || countryName.includes(normalizedSearch);
    });

    this.emptyResults = !this.filteredCities.length;

    if (this.emptyResults) {
      this.filteredCities = [...this.cities];
    }
  }

  normalizeText(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * @description
   * Reset search value and return full list of cities
   */
  resetSearchFilter(): void {
    this.searchModel = '';
    this.filteredCities = [...this.cities];
    this.cityIndex = this.filteredCities.findIndex(({ id }) => id === this?.city?.id);
  }

  initScrollbar() {
    if (!this.showCityPicker || !this.parentShadowRoot) {
      return;
    }

    setTimeout(() => {
      const scrollbarElem = this.parentShadowRoot?.querySelector('[data-simplebar]') as HTMLElement | undefined;

      if (scrollbarElem) {
        this.scrollbar = new SimpleBar(scrollbarElem);
      }
    });
  }

  unmountScrollbar() {
    setTimeout(() => {
      this.scrollbar?.unMount();
    });
  }

  scrollToSelectedCity(selector: string = '.widget__cities-list-city-label--selected') {
    // Ensure that the simple scrollbar directive is loaded
    setTimeout(() => {
      if (!this.simpleScrollBar) {
        return;
      }

      const selectedCityItem = this.simpleScrollBar.nativeElement.querySelector(selector) as HTMLElement;
      const simpleBarWrapper = this.simpleScrollBar.nativeElement.querySelector(
        '.simplebar-content-wrapper'
      ) as HTMLElement;

      if (!selectedCityItem) {
        simpleBarWrapper.scrollTop = 0;
        return;
      }

      const { scrollTop: simpleBarWrapperTop, clientHeight: simpleBarWrapperHeight } = simpleBarWrapper;
      const { offsetTop: cityItemTop, offsetHeight: cityItemHeight } = selectedCityItem;
      const simpleBarWrapperBottom = simpleBarWrapperTop + simpleBarWrapperHeight;
      const cityItemBottom = cityItemTop + cityItemHeight;

      // city item is inside the scroll
      if (cityItemTop >= simpleBarWrapperTop && cityItemBottom <= simpleBarWrapperBottom) {
        return;
      }

      // city item is outside the scroll, but above the current position
      if (cityItemTop < simpleBarWrapperTop) {
        // set the wrapper scroll position to be the same as city item top
        simpleBarWrapper.scrollTop = cityItemTop;
        return;
      }

      // cityItem is outside the scroll, but below the current position (and partially covered)
      if (cityItemBottom > simpleBarWrapperBottom) {
        // set the wrapper scroll position to be (vertical position of city inside the scroll - scroll height) + item height
        simpleBarWrapper.scrollTop = cityItemTop - simpleBarWrapperHeight + cityItemHeight;
        return;
      }
    }, 0);
  }

  trackByFilteredCities(index, item) {
    return item.id;
  }

  onArrowDown(event: KeyboardEvent) {
    if (this.cityIndex === this.filteredCities.length - 1) {
      return;
    }

    if (this.cityIndex > this.filteredCities.length - 1) {
      this.cityIndex = 0;
      return;
    }

    this.cityIndex += 1;
    this.scrollToSelectedCity('.widget__cities-list-city-label--active');
  }

  onArrowUp(event: KeyboardEvent) {
    if (this.cityIndex <= -1) {
      return;
    }

    this.cityIndex -= 1;
    this.scrollToSelectedCity('.widget__cities-list-city-label--active');
  }

  onEnterKey(event: KeyboardEvent) {
    if (this.cityIndex < 0 || this.cityIndex > this.filteredCities.length - 1) {
      return;
    }

    this.selectCity(this.filteredCities[this.cityIndex], this.cityIndex);
  }

  onEscKey(event: KeyboardEvent) {
    if (!this.showCityPicker) {
      return;
    }

    this.showHideCityPicker();
  }

  @HostListener('document:keyup.tab', ['$event'])
  onTabKey(event: KeyboardEvent) {
    event.preventDefault();
    this.onEscKey(event);
  }
}
